import { axios } from 'api'
import { useContext } from 'react'
import { CommonEntryContext } from './page_context'

export const LINK_SOURCE = new URLSearchParams(window.location.search).get(
  'source'
)

export const convertMdToText = (s) =>
  String(s)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/<img\b[^>]*>/gi, '')
    .replace(/!\[.*?\]\(.*?\)|!\[.*?\]\[.*?\]/g, '')
    .replace(/^\s*\[[^\]]+\]:\s+\S+.*$/gm, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\[(.*?)\]\(.*?\)|\[(.*?)\]\[.*?\]/g, (_, a, b) => a || b)
    .replace(/^#+\s*|^>\s?|^\s*[-*+]\s+|^\s*\d+\.\s+/gm, '')
    .replace(/\*\*|\*|__|_/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
export const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

export const useCommonEntryContext = () => {
  const {
    user = null,
    searchParams = null,
    setGlobalState = () => {},
    globalState = {},
    setNotFound = () => {},
    notAvailable = false,
    updateUser = () => {}
  } = useContext(CommonEntryContext) || {}
  return {
    user,
    searchParams,
    setGlobalState,
    globalState,
    setNotFound,
    notAvailable,
    updateUser
  }
}

export const CURRENT_ENV = window?.REACT_ENV_VARIABLES?.CURRENT_ENV || ''
export const OG_TITLE = 'ALO Wellness Club'
export const OG_DESCRIPTION =
  'The best online yoga, fitness, and meditation classes. Access thousands of videos from expert instructors in yoga, meditation, HIIT, barre, Pilates, and more. Mix up your workout routine with new classes every week. Start your 14-day free trial now!'

// Get a cookie value by name
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : undefined
}

export const capitalize = (str = '') => {
  if (!str) return ''
  str = String(str)
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Check if a cookie exists
export const hasCookie = (name) => {
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${encodeURIComponent(name)}=`))
}

export const isSafeRedirect = (path) => {
  if (typeof path !== 'string') return false
  if (!path.startsWith('/') || path.startsWith('//')) return false
  if (/^[^/]*:/.test(path)) return false
  if (path.includes('..') || /[\r\n]/.test(path)) return false
  const lower = path.toLowerCase()
  if (lower.startsWith('/javascript:') || lower.startsWith('/data:'))
    return false

  return /^\/[\w\-/]*$/.test(path)
}

// Set a cookie with name, value, and expiration in days
export const setCookie = (
  name,
  value,
  days = 7,
  domain = window.location.hostname
) => {
  const now = new Date()
  now.setTime(now.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; path=/; domain=${domain}; expires=${now.toUTCString()}; Secure; SameSite=Strict`
}

export const ALO_AUTH_TOKEN_STATE =
  window.REACT_ENV_VARIABLES?.ALO_AUTH_TOKEN_STATE

// Example: check for alo_auth_token
export const HAS_ALO_AUTH_TOKEN_STATE_COOKIE = hasCookie(ALO_AUTH_TOKEN_STATE)

export const PRODUCTION_URL = 'https://wellnessclub.aloyoga.com'

export const CUSTOMER_SUPPORT_EMAIL =
  window.REACT_ENV_VARIABLES?.CUSTOMER_SUPPORT_EMAIL
export const ORIGIN = window.location.origin
export const CURRENT_URL = `${ORIGIN}${window.location.pathname}`
export const SITE_DOMAIN = ORIGIN.replace(/^https?:\/\//, '')
export const NEW_NAVBAR_ITEMS = String(
  window.REACT_ENV_VARIABLES?.NEW_NAVBAR_ITEMS || ''
).split(',')

// Kill switch to hide the Events entry from the navbar (desktop + mobile).
// Defaults to enabled; set EVENTS_MENU_DISABLED=true to hide it.
export const EVENTS_MENU_DISABLED =
  String(window.REACT_ENV_VARIABLES?.EVENTS_MENU_DISABLED || '')
    .trim()
    .toLowerCase() === 'true'

export const isValidUrl = (url) => {
  if (!url) return false
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export const CALLBACK_URL = (() => {
  const params = new URLSearchParams(window.location.search)
  return params.get('redirect_to') || ''
})()

export const showHeaderAndFooterWebView = ({ iosAppClientId, iosAppScope }) => {
  const urlParams = new URLSearchParams(window.location.search)
  const isFromIosApp = Boolean(
    (iosAppClientId && iosAppScope) || urlParams.get('source') === 'iosApp'
  )
  const hideFor = [
    '/verify-alo',
    '/signup',
    '/all-free',
    '/share',
    '/referral'
  ].includes(window.location.pathname)
  return !Boolean(hideFor && isFromIosApp)
}

export const getPath = (input) => {
  try {
    const url = new URL(input, 'https://aloyoga.com')
    return url.pathname
  } catch {
    return ''
  }
}

export const redirect_to = (url) => (window.location.href = String(url))

export const redirect_to_with_cookie_creation = (url) => {
  axios
    .post('/alo_token_auth/create_cookie', {})
    .then(function (res) {
      window.location.href = String(url).replace(/(?<!https?:)\/\//g, '/')
    })
    .catch((err) => {
      console.log(err)
    })
}

export const sign_out_cookie_deletion = (url) => {
  axios
    .delete('/alo_token_auth/create_cookie', {})
    .then(() => {
      // ignore fetch errors
    })
    .catch((err) => {
      console.log(err)
    })
    .finally(() => {
      fetch(url, {
        method: 'GET',
        credentials: 'include'
      }).catch(() => {})
    })
}

// **Delete a cookie**
export const deleteCookie = (name, domain = window.location.hostname) => {
  // Setting the expiry date in the past removes the cookie
  document.cookie = `${encodeURIComponent(
    name
  )}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Strict`
}
